import type { ITrainerInvitationDto } from '@treneo/models';
import { PermissionCodes } from '@treneo/shared/constants';
import { useFormatDateTime } from '@treneo/shared/contexts';
import { useAppTranslations } from '@treneo/shared/i18n';
import { useAppParams } from '@treneo/shared/router';
import {
  AdminPageWrapper,
  Card,
  CardContent,
  ConfirmationDialog,
  DataGridWrapper,
  toast,
  useConfirmationDialog,
  useQueryErrorHandling,
  useServerPagination
} from '@treneo/shared/ui';

import { InviteTrainerInlineInput, RequireOrganizationPermission } from '../../components';
import {
  useCancelOrganizationInvitationMutation,
  useOrganizationInvitationsQuery,
  useOrganizationUsersQuery
} from '../../data';
import { handleMutationError } from '../../utils/error-handling.utils';
import { createOrganizationInvitationsColumns } from '../../utils/organization-invitations-page.utils';

const OrganizationInvitationsPageContent = () => {
  const { t } = useAppTranslations();
  const { formatDateTime } = useFormatDateTime();
  const { organizationId } = useAppParams<{ organizationId: string }>();
  const id = organizationId ?? '';
  const pagination = useServerPagination();
  const {
    openConfirm,
    closeConfirm,
    isOpen: isConfirmOpen,
    content: confirmContent,
    handleConfirm: handleConfirmYes
  } = useConfirmationDialog();
  const cancelInvitationMutation = useCancelOrganizationInvitationMutation();
  const { data: usersData } = useOrganizationUsersQuery(id, { enabled: !!id });
  const { data, isLoading, error, refetch } = useOrganizationInvitationsQuery(id, {
    request: { page: pagination.page, pageSize: pagination.pageSize },
    enabled: !!id
  });
  const { errorComponent } = useQueryErrorHandling({
    error: id ? error : undefined,
    refetch: () => {
      void refetch();
    }
  });
  const invitations = data?.invitations ?? [];
  const totalCount = data?.totalCount ?? 0;
  const users = usersData?.users ?? [];

  const handleCancelInvitation = (invitationId: string) => {
    if (!id) {
      return;
    }
    openConfirm({
      content: t('trainers.confirmCancelInvitation'),
      onConfirm: () => {
        cancelInvitationMutation.mutate(
          { organizationId: id, invitationId },
          {
            onSuccess: () => {
              toast.success(t('trainers.invitationCancelled'));
              void refetch();
            },
            onError: (err: unknown) => {
              handleMutationError(
                err,
                'organizations.errors.cancelOrganizationInvitationFailed',
                t
              );
              void refetch();
            }
          }
        );
      }
    });
  };

  const columns = createOrganizationInvitationsColumns({
    t,
    formatDateTime: (date, fallback) => formatDateTime(date, fallback),
    onCancelInvitation: handleCancelInvitation,
    isCancelling: cancelInvitationMutation.isPending,
    organizationId: id
  });

  if (errorComponent) {
    return errorComponent;
  }

  return (
    <AdminPageWrapper
      title={t('admin.organizationsMenu.invitations')}
      count={totalCount}
      countLabel={t('admin.invitationsCountsShort')}
      useCard={false}
    >
      <InviteTrainerInlineInput
        organizationId={id}
        users={users}
        onInviteSuccess={() => {
          void refetch();
        }}
      />

      <Card>
        <CardContent>
          <DataGridWrapper
            rows={invitations}
            columns={columns}
            loading={isLoading}
            {...pagination.gridPaginationProps(totalCount)}
            getRowId={(row: ITrainerInvitationDto) => row.id}
          />
        </CardContent>
      </Card>

      <ConfirmationDialog
        open={isConfirmOpen}
        content={confirmContent}
        onNo={closeConfirm}
        onYes={handleConfirmYes}
      />
    </AdminPageWrapper>
  );
};

export const OrganizationInvitationsPage = () => (
  <RequireOrganizationPermission permissionCode={PermissionCodes.ManageOrganization}>
    <OrganizationInvitationsPageContent />
  </RequireOrganizationPermission>
);
